<template>
  <div
    class="flex justify-center md:px-20 gap-2 md:gap-6 flex-wrap md:flex-nowrap"
  >
    <div class="w-1/2 flex justify-center">
      <ProgressCircle
        v-if="!isTeamChallenge && !isPassFailChallenge && !isClientChallenge"
        :steps="targetValue ?? 1"
        :progress="userProgressSumCurrentInterval"
        width="350"
        height="350"
        radius="145"
        start-degree="90"
        range-degree="360"
        stroke-width="26"
        show-progress
        challenge-type="normal"
      >
        <div class="flex flex-col items-center text-center">
          <span class="text-display1 font-normal">{{
            formatNumberOrZero(userProgressSum)
          }}</span>
          <span class="text-medium">{{ placeholder }}</span>
        </div>
      </ProgressCircle>

      <ProgressCircle
        v-else-if="isPassFailChallenge"
        :steps="1"
        :progress="userProgressSum > 0 ? 1 : 0"
        width="350"
        height="350"
        radius="145"
        start-degree="90"
        range-degree="360"
        stroke-width="26"
        show-progress
        challenge-type="normal"
      >
        <div class="flex flex-col items-center text-center">
          <span v-if="userProgressSum > 0" class="text-display2">{{
            $t("custom.challenge.track.yes.no.yes.button.caption")
          }}</span>
          <span
            v-if="userProgressSumCurrentInterval === null"
            class="text-display2"
            >{{
              $t("custom.challenge.track.yes.no.noEntry.button.caption")
            }}</span
          >
          <span
            v-else-if="formatNumberOrZero(userProgressSum) === 0"
            class="text-display2"
            >{{ $t("custom.challenge.track.yes.no.no.button.caption") }}</span
          >
        </div>
      </ProgressCircle>

      <ProgressCircle
        v-else-if="isTeamChallenge"
        :steps="allUsersProgressSum"
        :progress="progress"
        width="350"
        height="350"
        radius="145"
        start-degree="90"
        range-degree="360"
        stroke-width="26"
        show-progress
        challenge-type="normal"
      >
        <div class="flex flex-col items-center text-center">
          <span class="text-display1 font-normal">
            {{
              formatTeamChallengeValue(
                getTodaysProgressOfUser(competitiveChallenge)
              )
            }}</span
          >
          <span class="text-medium">{{
            t("comp.challenge.started.progress.top.text")
          }}</span>
          <div class="w-20 border-t border-gray-400 my-2"></div>
          <span class="text-display1 font-normal">{{
            formatTeamChallengeValue(allUsersProgressSum)
          }}</span>
          <span class="text-medium">{{
            t("comp.challenge.started.progress.bottom.text")
          }}</span>
        </div>
      </ProgressCircle>

      <ProgressCircle
        v-else-if="isClientChallenge"
        :steps="allUsersProgressSum"
        :progress="progress"
        width="350"
        height="350"
        radius="145"
        start-degree="90"
        range-degree="360"
        stroke-width="26"
        show-progress
        challenge-type="normal"
      >
        <div class="flex flex-col items-center text-center">
          <span class="text-display1 font-normal">
            {{ formatNumberOrZero(userProgressSum) }}</span
          >
          <span class="text-medium">{{
            t("comp.challenge.started.progress.top.text")
          }}</span>
          <div class="w-20 border-t border-gray-400 my-2"></div>
          <span class="text-display1 font-normal">{{
            formatNumberOrZero(allUsersProgressSum)
          }}</span>
          <span class="text-medium">{{
            t("custom.challenge.mandant.sum.label")
          }}</span>
        </div>
      </ProgressCircle>
    </div>
  </div>
</template>
<script setup lang="ts">
import ProgressCircle from "./ProgressCircle.vue";
import { useI18n } from "vue-i18n";
import { CompetitiveChallengeType } from "../../../enum/v2/competitive-challenge-type";
import {
  formatCompetitiveChallengeProgress,
  formatNumberOrZero,
} from "../../../plugins/NumberFormat";
const { t } = useI18n();
const props = defineProps({
  progress: {
    type: Number,
    default: 0,
  },
  targetValue: {
    type: Number,
  },
  userProgressSumCurrentInterval: {
    type: Number,
    default: 0,
  },
  allUsersProgressSumCurrentInterval: {
    type: Number,
    default: 0,
  },
  userProgressSum: {
    type: Number,
    default: 0,
  },
  isTeamChallenge: {
    type: Boolean,
    default: false,
  },
  isClientChallenge: {
    type: Boolean,
    default: false,
  },
  isPassFailChallenge: {
    type: Boolean,
    default: false,
  },
  allUsersProgressSum: {
    type: Number,
    default: 0,
  },
  averageProgressOfCurrentUser: {
    type: Number,
    default: 0,
  },
  userProgressToday: {
    type: Number,
    default: 0,
  },
  isYesPassFailChallenge: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
  },
  competitiveChallenge: {
    type: Object,
    default: () => ({}),
  },
});

const getTodaysProgressOfUser = (competitiveChallenge) => {
  const today = new Date();
  const day = today.getDate();
  const month = today.getMonth();
  const year = today.getFullYear();

  const todaysProgress = competitiveChallenge?.progressesOfCurrentUser?.find(
    (progress) => {
      const progressDate = new Date(progress.date);
      const progressDateDay = progressDate.getDate();
      const progressDateMonth = progressDate.getMonth();
      const progressDateYear = progressDate.getFullYear();

      return (
        progressDateDay === day &&
        progressDateMonth === month &&
        progressDateYear === year
      );
    }
  );

  return todaysProgress ? todaysProgress.value : 0;
};

const formatTeamChallengeValue = (value: unknown): string => {
  const challengeType = props.competitiveChallenge
    ?.competitiveChallengeType as CompetitiveChallengeType;

  return formatCompetitiveChallengeProgress(value, challengeType, {
    roundUp: true,
  });
};
</script>
